"use client";

import { LanguageLearningApp } from "@/components/app/LanguageLearningApp";
import { resolveAcquisitionDemoConfig } from "@/lib/acquisition-demo/config";
import { ACQUISITION_SPEAKING_DEMO_PRESET } from "@/lib/sessionLaunchPresets";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { AcquisitionDemoFrameReporter } from "./AcquisitionDemoFrameReporter";

type SavedLanguagePreferences = {
  nativeLanguageCode?: string;
  selectedLanguageCode?: string;
};

function readSavedLanguagePreferences(): SavedLanguagePreferences {
  try {
    const rawPreferences = window.localStorage.getItem("userLanguagePreferences");
    return rawPreferences ? (JSON.parse(rawPreferences) as SavedLanguagePreferences) : {};
  } catch {
    return {};
  }
}

export function AcquisitionSpeakingDemoApp() {
  const searchParams = useSearchParams();
  const [resolvedConfig, setResolvedConfig] = useState<
    ReturnType<typeof resolveAcquisitionDemoConfig> | undefined
  >();

  useEffect(() => {
    const savedPreferences = readSavedLanguagePreferences();
    const requireTargetSelection = searchParams.get("requireTargetSelection") === "true";
    setResolvedConfig(
      resolveAcquisitionDemoConfig({
        routeTargetLanguageCode: searchParams.get("target"),
        savedTargetLanguageCode: requireTargetSelection
          ? undefined
          : savedPreferences.selectedLanguageCode,
        routeInstructionLanguageCode: searchParams.get("instruction"),
        savedInstructionLanguageCode: savedPreferences.nativeLanguageCode,
        browserLanguages: navigator.languages,
      })
    );
  }, [searchParams]);

  if (!resolvedConfig) {
    return <div className="min-h-screen bg-[#0f0f14]" aria-label="Loading speaking demo" />;
  }

  return (
    <>
      <LanguageLearningApp
        launchPreset={ACQUISITION_SPEAKING_DEMO_PRESET}
        initialTargetLanguageParam={resolvedConfig.targetLanguage?.code}
        initialInstructionLanguageParam={resolvedConfig.instructionLanguage.code}
        initialLanguageLevelParam={resolvedConfig.languageLevel}
        initialSessionModeParam="conversation"
      />
      <AcquisitionDemoFrameReporter />
    </>
  );
}
